import memoizeOne from 'memoize-one'
import {
  filter,
  map,
  omit,
  path,
  pick,
  pipe,
  sortBy,
} from 'ramda'

import { ordinalize } from 'inflection'
import * as yup from 'yup'

import PesticideField from '~/src/IPM/Pesticide/Field'
import pesticideSchema from '~/src/IPM/Pesticide/validation'
import createLogger from '~/src/Lib/Logging'
import {
  attachmentValidationSchema,
  dateTimeSchema,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  getDateDiff,
  getDateTime,
  shallowEquals,
} from '~/src/Lib/Utils'
import MeasurementIcon from '~/src/UI/Icons/Measurement'
import SprayIcon from '~/src/UI/Icons/Spray'
import { FormikDatePicker as DatePicker } from '~/src/UI/Shared/Form/DatePicker'
import { FormikDateTimePicker as DateTimePicker } from '~/src/UI/Shared/Form/DateTimePicker'
import { FormikTextField as TextField } from '~/src/UI/Shared/Form/TextField'
import { FormikSelect as SharedSelect } from '~/src/UI/Shared/Select'

import { FACILITY_WIDE } from '../constants'

const displayName = 'Task/utils'
const logger = createLogger(displayName)

export const TIME_UNITS = ['minutes', 'hours', 'days', 'weeks']

const DUPLICATE_OMIT_FIELDS = [
  'attachment',
  'createdBy',
  'createdOn',
  'completedBy',
  'completedOn',
  'id',
  'startedBy',
  'startedOn',
  'ts',
]

const typeToSchema = {
  date: dateTimeSchema,
  datetime: dateTimeSchema,
  string: yup.string(),
  integer: yup.number().integer(),
}

export const nameToSchema = {
  pesticide: pesticideSchema.nullable()
}

export const nameToFieldComponent = {
  pesticide: PesticideField,
  dataType: SharedSelect
}

export const typeToFieldComponent = {
  string: TextField,
  integer: TextField,
  date: DatePicker,
  datetime: DateTimePicker,
  array: SharedSelect
}

export const getCombinedProps = memoizeOne(({ facilityMemberships: memberships, ...connectedProps }, props) => ({
  ...connectedProps,
  memberships,
  ...props,
}))

export const BEHAVIOR_TO_ICON = Object.freeze({
  PEST: SprayIcon,
  READING_CHECKLIST: MeasurementIcon
})

export const BEHAVIOR_TO_LABEL = Object.freeze({
  PEST: 'IPM Application',
  READING_CHECKLIST: 'Manual Reading Checklist'
})

export const prepareMetadataLabel = label => {
  if (!label) return false
  const splitName = []
  label.split(' ').map((word, index) => {
    if (index !== 0) {
      return splitName.push(` ${word.toLowerCase()}`)
    }
    return splitName.push(`${word}`)
  })
  return splitName
}

export const getFieldName = (name, prefix = null) => (prefix ? `${prefix}.${name}` : name)

export const prepareTaskTitle = memoizeOne((newCategory, oldCategory, title) => {
  const { taskBehavior, name } = newCategory
  const { taskBehavior: oldBehavior, name: oldName } = oldCategory
  const titleIsAuto = oldBehavior ? title === oldName : !title

  if ((!taskBehavior && !oldBehavior) || !titleIsAuto) return title || ''

  return taskBehavior ? name : ''
})

export const fieldPropsPicker = pick(['disabled', 'name', 'label', 'margin', 'component', 'fullWidth'])

export const prepareFields = memoizeOne((disableStep3, properties, opts = { prefix: 'metadata', prefixNames: true }) => pipe(
  Object.entries,
  filter(([, { inherited, type }]) => (
    !inherited
    && (Array.isArray(type) ? type.some(t => t in typeToFieldComponent) : type in typeToFieldComponent)
  )),
  sortBy(path([1, 'sequence'])),
  map(([key, config]) => {
    const { type } = config
    const name = opts.prefixNames ? `${opts.prefix}.${key}` : key
    let component
    if (key in nameToFieldComponent) {
      component = nameToFieldComponent[key]
    }
    if (!component) {
      component = Array.isArray(type)
        ? typeToFieldComponent[type.find(t => t in typeToFieldComponent)]
        : typeToFieldComponent[type]
    }

    return {
      ...config,
      name,
      component,
      disabled: disableStep3,
      margin: 'dense',
      fullWidth: true,
    }
  })
)(properties))

/**
 * @param {Object} props
 * @param {string} props.action
 * @param {string} props.category
 * @param {Object} props.currentFacility
 * @param {Date|import('luxon').DateTime} props.date
 * @param {{ params: { date: string, id: string } }} props.dialogRouteInfo
 * @param {Object} props.pesticides
 * @param {Object<number, Array<{startDate: string, endDate: string}>>} props.phasesByHarvest
 * @param {number|{ id: number }} props.room
 * @param {{ params: { id: string, url: string } }} props.routeInfo
 * @returns {Object} task form initial values
 */
export const getInitialValues = memoizeOne(props => {
  const {
    action,
    category: categoryProp = null,
    currentFacility,
    date,
    dialogRouteInfo = null,
    pesticides,
    phasesByHarvest,
    room: roomProp,
    routeInfo = null,
    task: storeTask,
    taskId: propsId,
    tasks,
    taskCategories,
    zone,
  } = props
  const { params } = dialogRouteInfo ?? EMPTY_OBJECT
  const taskId = params?.id ?? propsId

  let task = tasks[taskId] ?? storeTask

  if (action === 'new') {
    task = {}
    if (typeof routeInfo?.url === 'string' && routeInfo.url.includes('harvests') && params?.date) {
      const urlDt = getDateTime(params.date)
      const harvestId = routeInfo.params.id
      const phases = phasesByHarvest[harvestId]

      const phase = phases?.find(p => {
        const startDt = getDateTime(p.startDate).startOf('day')
        const endDt = getDateTime(p.endDate).startOf('day')

        return startDt.until(endDt).contains(urlDt)
      })

      const startDateDiff = phase ? getDateDiff(phase.startDate, urlDt.startOf('day')) : null

      task.relativeStartDay = startDateDiff?.daysDiff
      task.phase = phase?.id
    }
  }
  const { assigned = null, assignees = EMPTY_ARRAY, category: categoryValue = null, dueDate } = task

  const ts = getDateTime('now')
  let dueDateNew = dueDate ?? date ? getDateTime(dueDate ?? date) : null

  if (action === 'duplicate') {
    task = omit(DUPLICATE_OMIT_FIELDS, task)
    dueDateNew = null
  }

  let categoryId = categoryValue
    ? (categoryValue in taskCategories && categoryValue)
    : Object.values(taskCategories).find((c = EMPTY_OBJECT) => c.taskBehavior === categoryProp || c.id === categoryValue)?.id
  if (!categoryId) {
    categoryId = Object.values(taskCategories).find(
      (c = EMPTY_OBJECT) => c.behavior === 'TASK' && !c.taskBehavior
    )?.id
  }

  let metadata = task.metadata ?? null
  if (task && task.metadata && task.metadata.pesticide) {
    const { [task.metadata.pesticide]: pesticide } = pesticides
    metadata = { ...metadata, pesticide }
  }
  const facilityWide = (task?.id || action === 'duplicate') && !task?.room && !(currentFacility.continuous && task.phase)

  return {
    // default task values
    title: '',
    description: '',
    descriptionMentions: [],
    priority: 2,
    // spread task to override any defined values
    ...task,
    assigned,
    assignees,
    attachment: task.attachment ?? EMPTY_ARRAY,
    category: categoryId,
    dueDate: dueDateNew,
    metadata,
    relativeStartDay: task.relativeStartDay,
    room: facilityWide ? FACILITY_WIDE : task.room ?? roomProp?.id ?? roomProp,
    ts: task.ts ?? ts,
    zone: task.zone ?? zone?.id ?? zone,
    reminderUnit: task.remindAt ? task.reminderUnit ?? '' : '',
    reminderAmount: task.remindAt ? task.reminderAmount ?? '' : '',
  }
}, shallowEquals)

/* eslint-disable no-template-curly-in-string */
export const getValidationSchema = memoizeOne(props => {
  const { currentFacility, taskCategories, rooms, zones } = props
  return yup.object({
    id: yup.number().integer().nullable(),
    title: yup.string().trim()
      .required('Please enter a title.')
      .min(2, 'Title needs at least two (2) characters.'),
    category: yup.number().integer().test(
      'is-validCategory',
      'please choose a valid task category.',
      value => {
        if (value == null) return true
        return value in taskCategories
      }
    )
      .typeError('please choose a category'),
    assigned: yup.number().integer().min(1).nullable(),
    assignees: yup.array().of(yup.object().test({
      name: 'is-validAssignee',
      exclusive: true,
      test: (value, ctx) => {
        if (value.membership == null) return true
        return Number(value.membership) > 0
          || ctx.createError({
            message: `${ordinalize(String(ctx.parent.indexOf(value) + 1))} assignee is not valid.`
          })
      }
    }))
      .nullable(),
    room: yup.mixed().test(
      'is-validRoom',
      'Please choose a room in this facility.',
      value => {
        if (currentFacility?.continuous) return true
        return value === FACILITY_WIDE || value in rooms
      }
    ),
    zone: yup.lazy(value => {
      const invalidZoneMsg = 'Please choose a valid zone.'
      if (value == null) return yup.mixed().nullable().optional()
      return yup.number().integer().when('room', ([room], schema) => {
        if (!currentFacility?.continuous) {
          return schema.oneOf(
            room in rooms
              ? rooms[room]?.zones ?? EMPTY_ARRAY
              : Object.keys(zones).map(Number),
            invalidZoneMsg
          )
        }
        return schema.oneOf(Object.keys(zones).map(Number), invalidZoneMsg)
      }).typeError(invalidZoneMsg)
    }),
    dueDate: dateTimeSchema.nullable().when('startDate', ([startDate], schema) => (
      startDate ? schema.min(startDate, 'must not be before start date') : schema
    )).test({
      name: 'is-validDueDate',
      message: 'Due date must be in the future',
      test: (value, ctx) => {
        if (!ctx.parent.id && value) {
          return getDateTime('now') < getDateTime(value)
        }
        return true
      }
    }),
    attachment: attachmentValidationSchema,
    metadata: yup.object().nullable().when('category', ([categoryId], schema) => {
      const { [categoryId]: category } = taskCategories
      if (!category?.taskBehavior) return schema

      if (category && category.schema?.properties) {
        return schema.shape(Object.entries(category.schema.properties).reduce(
          (acc, [name, config]) => {
            let propSchema
            const { type } = config
            if (name in nameToSchema) {
              propSchema = nameToSchema[name]
            } else if (type in typeToSchema) {
              propSchema = typeToSchema[type]
            } else if (Array.isArray(type) && type.some(t => t in typeToSchema)) {
              propSchema = typeToSchema[type.find(t => t !== 'null')]
              if (type.find(t => t === 'null')) {
                propSchema = propSchema.nullable()
              }
            }

            return propSchema ? { ...acc, [name]: propSchema } : acc
          },
          EMPTY_OBJECT
        ))
      }
      return schema
    }),
    reminderAmount: yup
      .mixed()
      .nullable()
      .when(['dueDate', 'reminderUnit'], ([dueDate, reminderUnit], schema) => (dueDate && reminderUnit
        ? yup.number().integer().max(
          Math.floor(getDateTime(dueDate).diffNow(reminderUnit).as(reminderUnit)),
          'Please schedule the reminder in the future'
        )
        : schema)),
  })
}, shallowEquals)
/* eslint-enable no-template-curly-in-string */
